import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Megaphone, ArrowLeft, Save, Loader2, Check, X, AlertTriangle,
  Calendar, DollarSign, Target, Tag, FileText, TrendingUp, Users,
  BarChart3, Zap, Globe, Mail, Film, ChevronDown,
} from "lucide-react";

const CAMPAIGN_TYPES = [
  { value: "multi", label: "Multi-Channel", icon: Globe, color: "text-blue-600 bg-blue-50", desc: "Run across all platforms" },
  { value: "social", label: "Social Media", icon: Users, color: "text-pink-600 bg-pink-50", desc: "Facebook, Instagram, X, etc." },
  { value: "email", label: "Email Campaign", icon: Mail, color: "text-purple-600 bg-purple-50", desc: "Newsletters & sequences" },
  { value: "ads", label: "Paid Advertising", icon: DollarSign, color: "text-green-600 bg-green-50", desc: "Google, Meta, LinkedIn ads" },
  { value: "content", label: "Content Marketing", icon: FileText, color: "text-amber-600 bg-amber-50", desc: "Blog posts, articles, SEO" },
  { value: "video", label: "Video Campaign", icon: Film, color: "text-red-600 bg-red-50", desc: "YouTube, Reels, TikTok" },
];

// ── Toast ──
function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === "success" ? "bg-green-600" : "bg-red-600"}`}>
      {type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{message: string; type: "success"|"error"} | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("multi");
  const [budget, setBudget] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tags, setTags] = useState("");
  const [goals, setGoals] = useState<string[]>([]);
  const [showGoals, setShowGoals] = useState(false);

  const GOAL_OPTIONS = [
    "Increase brand awareness", "Generate leads", "Drive website traffic",
    "Boost engagement", "Increase sales", "Grow email list",
    "Launch new product", "Build community", "Improve retention",
  ];

  const toggleGoal = (g: string) => {
    setGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const handleSave = async () => {
    if (!name.trim()) { setToast({ message: "Campaign name is required", type: "error" }); return; }
    setSaving(true);
    try {
      const payload: any = {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        budget: budget ? parseFloat(budget) : 0,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        goals: goals.length ? goals : undefined,
        tags: tags.trim() || undefined,
      };
      const result = await api.post<any>("/campaigns", payload);
      setToast({ message: "Campaign created successfully!", type: "success" });
      setTimeout(() => navigate(`/campaigns/${result.id}`), 1200);
    } catch (err: any) {
      setToast({ message: err.message || "Failed to create campaign", type: "error" });
    }
    setSaving(false);
  };

  const selectedType = CAMPAIGN_TYPES.find(t => t.value === type);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/campaigns")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
            <p className="text-sm text-gray-500 mt-0.5">Set up a new marketing campaign</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/campaigns")}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="btn-primary text-sm flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create Campaign
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Name & Description */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Campaign Name <span className="text-red-500">*</span></label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g., Summer Product Launch 2026"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Description</label>
              <textarea
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Describe the campaign objective, target audience, and key messaging..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
              />
            </div>
          </div>

          {/* Campaign Type */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <label className="block text-sm font-semibold text-gray-900">Campaign Type</label>
            <div className="grid grid-cols-2 gap-3">
              {CAMPAIGN_TYPES.map(t => (
                <button key={t.value} onClick={() => setType(t.value)}
                  className={`flex items-start gap-3 p-4 rounded-xl text-left transition-all border ${
                    type === t.value ? "ring-2 ring-purple-500 border-purple-200 bg-purple-50/30" : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                  }`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${t.color}`}>
                    <t.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${type === t.value ? "text-purple-700" : "text-gray-900"}`}>{t.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Goals */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setShowGoals(!showGoals)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-purple-600" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Campaign Goals</p>
                  <p className="text-xs text-gray-500">{goals.length ? `${goals.length} selected` : "Select your objectives"}</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showGoals ? "rotate-180" : ""}`} />
            </button>
            {showGoals && (
              <div className="px-6 pb-5 flex flex-wrap gap-2">
                {GOAL_OPTIONS.map(g => (
                  <button key={g} onClick={() => toggleGoal(g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      goals.includes(g)
                        ? "bg-purple-600 text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}>
                    {g}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Budget */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <DollarSign className="w-4 h-4 text-green-600" /> Budget
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number" value={budget} onChange={e => setBudget(e.target.value)}
                placeholder="0.00" min="0" step="100"
                className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            {budget && parseFloat(budget) > 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Budget: ${parseFloat(budget).toLocaleString()}
              </p>
            )}
          </div>

          {/* Date Range */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Calendar className="w-4 h-4 text-purple-600" /> Schedule
            </label>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none" />
              </div>
            </div>
            {startDate && endDate && (
              <p className="text-xs text-purple-600">
                Duration: {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000)} days
              </p>
            )}
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Tag className="w-4 h-4 text-amber-600" /> Tags
            </label>
            <input
              type="text" value={tags} onChange={e => setTags(e.target.value)}
              placeholder="product-launch, q2, priority"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
            />
            <p className="text-xs text-gray-400">Comma separated tags for organization</p>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100 p-5 space-y-3">
            <h4 className="text-sm font-semibold text-purple-900">Campaign Summary</h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="font-medium text-gray-900">{selectedType?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Budget</span>
                <span className="font-medium text-gray-900">{budget ? `$${parseFloat(budget).toLocaleString()}` : "Not set"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Goals</span>
                <span className="font-medium text-gray-900">{goals.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Schedule</span>
                <span className="font-medium text-gray-900">{startDate ? "Set" : "Not set"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
